// src/components/Organization/EmailLayoutSettings.tsx

// What? Organization-level email layout and branding configuration.
// Why? To let org admins customize the visual wrapper around all outgoing emails.
// How? Loads/saves layout settings via the email layout API with a live preview strip.

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Alert,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  CircularProgress,
  Divider,
} from "@mui/joy";
import PaletteIcon from "@mui/icons-material/Palette";
import SaveIcon from "@mui/icons-material/Save";
import { useAuthFetch } from "../../utils/useAuthFetch";
import { useOrgEmailStatus } from "../../hooks/useOrgEmailStatus";
import DisabledEmailOverlay from "./DisabledEmailOverlay";

interface EmailLayoutSettingsProps {
  organizationId: string;
}

interface LayoutConfig {
  logo_url: string;
  header_color: string;
  header_text_color: string;
  footer_text: string;
  legal_disclaimer: string;
}

const DEFAULT_LAYOUT: LayoutConfig = {
  logo_url: "",
  header_color: "#1a1a2e",
  header_text_color: "#ffffff",
  footer_text: "Sent via X-Machine",
  legal_disclaimer: "",
};

const ColorSwatch: React.FC<{ color: string }> = ({ color }) => (
  <Box
    sx={{
      width: 24,
      height: 24,
      borderRadius: "xs",
      border: "1px solid",
      borderColor: "neutral.outlinedBorder",
      bgcolor: color,
      flexShrink: 0,
    }}
  />
);

const EmailLayoutSettings: React.FC<EmailLayoutSettingsProps> = ({
  organizationId,
}) => {
  const authFetch = useAuthFetch();
  const { enabled: emailEnabled, loading: emailStatusLoading } =
    useOrgEmailStatus(organizationId);
  const [layout, setLayout] = useState<LayoutConfig>(DEFAULT_LAYOUT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "danger";
    message: string;
  } | null>(null);

  const loadLayout = useCallback(async () => {
    try {
      setLoading(true);
      const data = await authFetch(
        `/api/email/organization/${organizationId}/layout`,
        "fetch email layout",
      );
      if (data?.layout) {
        setLayout({ ...DEFAULT_LAYOUT, ...data.layout });
      }
    } catch {
      // Not configured yet
    } finally {
      setLoading(false);
    }
  }, [authFetch, organizationId]);

  useEffect(() => {
    loadLayout();
  }, [loadLayout]);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await authFetch(
        `/api/email/organization/${organizationId}/layout`,
        "save email layout",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(layout),
        },
      );
      setFeedback({ type: "success", message: "Email layout saved" });
    } catch (err) {
      setFeedback({ type: "danger", message: `Failed to save: ${err}` });
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback(null), 5000);
    }
  };

  const updateField = <K extends keyof LayoutConfig>(
    field: K,
    value: LayoutConfig[K],
  ) => {
    setLayout((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size="sm" />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const body = (
    <Stack spacing={2.5}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <PaletteIcon sx={{ color: "primary.500" }} />
        <Typography level="title-lg">Email Layout & Branding</Typography>
      </Box>

      {/* Logo URL */}
      <FormControl>
        <FormLabel>Logo URL</FormLabel>
        <Input
          size="sm"
          placeholder="https://your-company.com/logo.png"
          value={layout.logo_url}
          onChange={(e) => updateField("logo_url", e.target.value)}
        />
      </FormControl>

      {/* Colors */}
      <Stack direction="row" spacing={2}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Header Background</FormLabel>
          <Stack direction="row" spacing={1} alignItems="center">
            <ColorSwatch color={layout.header_color} />
            <Input
              size="sm"
              sx={{ flex: 1 }}
              value={layout.header_color}
              onChange={(e) => updateField("header_color", e.target.value)}
              placeholder="#1a1a2e"
            />
          </Stack>
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Header Text Color</FormLabel>
          <Stack direction="row" spacing={1} alignItems="center">
            <ColorSwatch color={layout.header_text_color} />
            <Input
              size="sm"
              sx={{ flex: 1 }}
              value={layout.header_text_color}
              onChange={(e) => updateField("header_text_color", e.target.value)}
              placeholder="#ffffff"
            />
          </Stack>
        </FormControl>
      </Stack>

      {/* Footer / Legal */}
      <FormControl>
        <FormLabel>Footer Text</FormLabel>
        <Input
          size="sm"
          value={layout.footer_text}
          onChange={(e) => updateField("footer_text", e.target.value)}
          placeholder="Sent via X-Machine"
        />
      </FormControl>

      <FormControl>
        <FormLabel>Legal Disclaimer</FormLabel>
        <Textarea
          minRows={2}
          size="sm"
          value={layout.legal_disclaimer}
          onChange={(e) => updateField("legal_disclaimer", e.target.value)}
          placeholder="Optional legal text appended to all emails"
        />
      </FormControl>

      <Divider />

      {/* Live Preview Strip */}
      <Box>
        <Typography level="body-sm" sx={{ mb: 1, fontWeight: 600 }}>
          Preview
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "sm",
            overflow: "hidden",
          }}
        >
          {/* Header preview */}
          <Box
            sx={{
              bgcolor: layout.header_color,
              color: layout.header_text_color,
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minHeight: 48,
            }}
          >
            {layout.logo_url && (
              <Box
                component="img"
                src={layout.logo_url}
                alt="Logo"
                sx={{ height: 32, maxWidth: 120, objectFit: "contain" }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            )}
            <Typography
              sx={{
                color: layout.header_text_color,
                fontWeight: 600,
                fontSize: "14px",
              }}
            >
              Email Subject Line
            </Typography>
          </Box>

          {/* Body placeholder */}
          <Box sx={{ p: 2, bgcolor: "#ffffff", minHeight: 60 }}>
            <Typography
              level="body-sm"
              sx={{ color: "#666", fontStyle: "italic" }}
            >
              Email content area...
            </Typography>
          </Box>

          {/* Footer preview */}
          <Box
            sx={{
              bgcolor: "#f8f9fa",
              p: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
              textAlign: "center",
            }}
          >
            <Typography level="body-xs" sx={{ color: "#6c757d" }}>
              {layout.footer_text || "Footer text"}
            </Typography>
            {layout.legal_disclaimer && (
              <Typography
                level="body-xs"
                sx={{ color: "#999", mt: 0.5, fontSize: "11px" }}
              >
                {layout.legal_disclaimer}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Feedback */}
      {feedback && (
        <Alert color={feedback.type} size="sm">
          {feedback.message}
        </Alert>
      )}

      {/* Save */}
      <Stack direction="row" spacing={1.5}>
        <Button
          size="sm"
          startDecorator={<SaveIcon />}
          loading={saving}
          onClick={handleSave}
        >
          Save Layout
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Card variant="outlined">
      <CardContent>
        {!emailStatusLoading && !emailEnabled ? (
          <DisabledEmailOverlay>{body}</DisabledEmailOverlay>
        ) : (
          body
        )}
      </CardContent>
    </Card>
  );
};

export default EmailLayoutSettings;
